import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { AgentAvatar } from "./AgentAvatar.js";
import { agentById, type AgentId } from "./nav.js";
import {
	relativeTime,
	tierShort,
	type PendingApproval,
} from "./sidebar-stubs.js";

type Props = {
	approvals: PendingApproval[];
	onMessageAgent: (id: AgentId) => void;
};

/** Pending gates — stub until Approval DO is bound into Matrix. */
export function SidebarNeedsYou({ approvals, onMessageAgent }: Props) {
	const [selected, setSelected] = useState<PendingApproval | null>(null);
	const [stubNote, setStubNote] = useState<string | null>(null);

	return (
		<section className="section" aria-label="Needs you">
			<div className="section-head">
				<h2 className="section-title">Needs you</h2>
				<span className="section-meta">{approvals.length}</span>
			</div>

			{approvals.length === 0 ? (
				<p className="side-empty">
					Nothing waiting for your approval.
				</p>
			) : (
				<ul className="side-list side-list--approvals">
					{approvals.map((item) => {
						const agent = agentById(item.agentId);
						return (
							<li key={item.id}>
								<button
									type="button"
									className="side-approval-row"
									onClick={() => {
										setStubNote(null);
										setSelected(item);
									}}
								>
									<span className="side-item-mark">
										<AgentAvatar agent={agent} size="sm" />
									</span>
									<span className="side-approval-copy">
										<span className="side-approval-summary">
											{item.summary}
										</span>
										<span className="side-approval-meta">
											<span>{agent.name}</span>
											<span aria-hidden>·</span>
											<Badge
												variant="outline"
												className="h-4 px-1 text-[9px]"
											>
												{tierShort(item.tier)}
											</Badge>
											{item.estimatedCostUsd != null && (
												<>
													<span aria-hidden>·</span>
													<span>
														${item.estimatedCostUsd}
													</span>
												</>
											)}
											<span aria-hidden>·</span>
											<span>{relativeTime(item.createdAt)}</span>
										</span>
									</span>
								</button>
							</li>
						);
					})}
				</ul>
			)}

			<Dialog
				open={selected !== null}
				onOpenChange={(open) => {
					if (!open) {
						setSelected(null);
						setStubNote(null);
					}
				}}
			>
				{selected && (
					<DialogContent className="gap-0 p-0 sm:max-w-md">
						<DialogHeader className="gap-2 border-b border-mx-sep px-6 pt-6 pb-4 text-left">
							<div className="flex items-start gap-3">
								<AgentAvatar
									agent={agentById(selected.agentId)}
									size="md"
								/>
								<div className="min-w-0">
									<p className="m-0 text-[11px] font-semibold tracking-[0.06em] text-mx-tertiary uppercase">
										{tierShort(selected.tier)} ·{" "}
										{agentById(selected.agentId).name}
									</p>
									<DialogTitle className="mt-0.5 text-lg tracking-[-0.02em]">
										Approval needed
									</DialogTitle>
								</div>
							</div>
							<DialogDescription className="text-[13px] leading-relaxed text-mx-secondary">
								{selected.summary}
								{selected.estimatedCostUsd != null
									? ` · ~$${selected.estimatedCostUsd}`
									: ""}
							</DialogDescription>
						</DialogHeader>

						<div className="px-6 py-4">
							<p className="m-0 text-[12px] leading-relaxed text-mx-tertiary">
								Review the request, then approve or deny. Decisions
								also sync to your connected channels.
							</p>
							{stubNote && (
								<p
									className="mt-3 m-0 rounded-lg border border-mx-sep bg-mx-sidebar px-3 py-2 text-[12px] text-mx-secondary"
									role="status"
								>
									{stubNote}
								</p>
							)}
						</div>

						<DialogFooter className="gap-2 border-t border-mx-sep px-6 py-4 sm:justify-between">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => {
									const id = selected.agentId;
									setSelected(null);
									onMessageAgent(id);
								}}
							>
								Message {agentById(selected.agentId).name}
							</Button>
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={() =>
										setStubNote(
											"Deny isn’t available in this preview yet.",
										)
									}
								>
									Deny
								</Button>
								<Button
									type="button"
									onClick={() =>
										setStubNote(
											"Approve isn’t available in this preview yet.",
										)
									}
								>
									Approve
								</Button>
							</div>
						</DialogFooter>
					</DialogContent>
				)}
			</Dialog>
		</section>
	);
}
